// Cadeau bij abonnement (Arctic Blue) — voegt het gratis product als losse
// regel toe zodra de bezoeker een abonnement heeft toegevoegd.
//
// Eén cadeau per abonnementsproduct: sluit de klant in één bestelling twee
// abonnementen af (bv. capsules én gummies), dan krijgt hij ook twee cadeaus.
// De cadeauregel draagt in de property _ab_cadeau de handle van het product
// waar hij bij hoort; snippets/cart-products.liquid zet die handle op de rij
// als data-ab-cadeau-for en op de abonnementsrij als data-ab-abo-for, zodat
// beide kanten hier per product te matchen zijn.
//
// Werkwijze: de normale product-form-flow blijft ongemoeid. Bij de submit van
// het productformulier (met abonnement gekozen) start de cadeau-add al
// PARALLEL met de add van de base; het geslaagde CartAddEvent van de base
// wacht daarna alleen nog op dat resultaat. Zo verschijnt het cadeau
// vrijwel gelijktijdig met de abonnementsregel in de drawer. De Discount
// Function "staffelkorting" maakt die regel 100% gratis, maar alleen als het
// aantal 1 is en er ook echt een abonnementsregel in de cart zit.
//
// Vangnetten: mislukt de add van de base (bv. voorraad), dan draait
// onCartError de al toegevoegde cadeauregel terug; bleek er toch al een
// cadeauregel voor dit product te zijn (add.js voegt aantallen dan samen),
// dan zet het quantity-vangnet de regel terug op 1.
//
// Bewust GEEN selling_plan op de cadeauregel: daardoor gaat het cadeau alleen
// mee met de eerste bestelling en niet met de herhaalleveringen.
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from "@theme/events";

function themeRoutes() {
  return (window.DiekTheme && window.DiekTheme.routes) || {};
}

// routes.cart_change_url en cart_update_url hebben géén .js-suffix
// (cart en cart_add_url wél) — normaliseren dus.
function withJs(url, fallback) {
  let u = url || fallback;
  if (!u.endsWith(".js")) u += ".js";
  return u;
}

function cartItemsSectionIds() {
  const ids = new Set();
  document.querySelectorAll("cart-items-component[data-section-id]").forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

function cadeauFormData(variantId, giftFor) {
  const fd = new FormData();
  fd.append("id", variantId);
  fd.append("quantity", "1");
  fd.append("properties[_ab_cadeau]", giftFor || "1");
  const sectionIds = cartItemsSectionIds();
  if (sectionIds.length > 0) fd.append("sections", sectionIds.join(","));
  return fd;
}

function cssEscape(value) {
  if (window.CSS && typeof CSS.escape === "function") return CSS.escape(value);
  return String(value).replace(/["\\]/g, "\\$&");
}

/** Selector voor de cadeauregel(s) van één abonnementsproduct. */
function cadeauRowSelector(giftFor) {
  if (!giftFor) return "[data-ab-cadeau-row]";
  return '[data-ab-cadeau-row][data-ab-cadeau-for="' + cssEscape(giftFor) + '"]';
}

/** Selector voor de abonnementsregel(s) van één product. */
function aboRowSelector(giftFor) {
  if (!giftFor) return "[data-ab-abo-row]";
  return '[data-ab-abo-row][data-ab-abo-for="' + cssEscape(giftFor) + '"]';
}

/** Is deze cart.js-regel het cadeau van het product met handle giftFor? */
function isCadeauVoor(item, giftFor) {
  const val = item.properties && item.properties._ab_cadeau;
  if (!val) return false;
  return !giftFor || String(val) === String(giftFor);
}

/**
 * Een cadeauregel is wees als er geen abonnementsregel (meer) is van het
 * product waar hij bij hoort. Oudere regels zonder producthandle ("1")
 * blijven zolang er überhaupt nog een abonnement in de cart zit.
 */
function isWees(giftFor, aboHandles) {
  if (giftFor && giftFor !== "1") return !aboHandles.has(giftFor);
  return aboHandles.size === 0;
}

function sectionsDoc(sections) {
  const html = Object.values(sections).join("");
  return new DOMParser().parseFromString(html, "text/html");
}

class AbCadeauComponent extends HTMLElement {
  connectedCallback() {
    this.root = this.closest(".ab-so");
    this._onCartAdd = this.onCartAdd.bind(this);
    this._onSubmit = this.onFormSubmit.bind(this);
    this._onCartError = this.onCartError.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this._onCartAdd);
    document.addEventListener(ThemeEvents.cartError, this._onCartError);

    // Het productformulier waar het abo-blok zijn hidden inputs aan koppelt.
    const planInput = this.root && this.root.querySelector("[data-ab-selling-plan]");
    const formId = planInput && planInput.getAttribute("form");
    this._form = formId ? document.getElementById(formId) : null;
    if (this._form) this._form.addEventListener("submit", this._onSubmit);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this._onCartAdd);
    document.removeEventListener(ThemeEvents.cartError, this._onCartError);
    if (this._form) this._form.removeEventListener("submit", this._onSubmit);
  }

  /**
   * Waar of de bezoeker op dit moment het abonnement afsluit mét de
   * frequentie waar de cadeautegel bij staat (data-gift-plan-id = de
   * aanbevolen/beste frequentie uit het blok). Andere frequenties krijgen
   * géén cadeau; de tegel belooft het daar ook niet.
   */
  isSubscribing() {
    if (!this.root) return false;
    const plan = this.root.querySelector("[data-ab-selling-plan]");
    if (!plan || !plan.value) return false;
    const giftPlanId = this.dataset.giftPlanId;
    if (!giftPlanId) return false;
    return String(plan.value) === String(giftPlanId);
  }

  /** Start de cadeau-add parallel met de add van de base. */
  onFormSubmit() {
    if (this._pending) return;
    if (!this.isSubscribing()) return;
    const variantId = this.dataset.giftVariantId;
    if (!variantId) return;
    // Al een cadeauregel voor dít product in de cart? (Elke morph houdt de
    // cart-DOM actueel.) Dan niets starten; het quantity-vangnet hieronder
    // dekt de rest af. Cadeaus van andere abonnementsproducten tellen niet.
    if (document.querySelector(cadeauRowSelector(this.dataset.giftFor))) return;

    const routes = themeRoutes();
    this._pending = fetch(routes.cart_add_url || "/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: cadeauFormData(variantId, this.dataset.giftFor),
    }).then((r) => {
      if (!r.ok) throw new Error("cart/add failed: " + r.status);
      return r.json();
    });
    // Afhandeling (ook van fouten) gebeurt in onCartAdd/onCartError.
    this._pending.catch(() => {});
  }

  async onCartAdd(event) {
    const data = (event.detail && event.detail.data) || {};
    if (data.source === "ab-cadeau") return;
    if (data.source !== "product-form-component") return;
    if (data.didError) return; // foutpad loopt via onCartError en de opruimer

    const variantId = this.dataset.giftVariantId;
    if (!variantId) return;
    const giftFor = this.dataset.giftFor;

    const pending = this._pending;
    this._pending = null;
    // Zonder parallelle add alleen iets doen als er nu een abonnement gekozen
    // is; een wél gestarte add altijd afhandelen (de cart bevat dan een
    // abonnementsregel, dus de cadeauregel is legitiem).
    if (!pending && !this.isSubscribing()) return;

    if (this._adding) {
      if (pending) this._pending = pending;
      return;
    }
    this._adding = true;

    try {
      const routes = themeRoutes();
      let addData = null;

      if (pending) {
        try {
          addData = await pending;
        } catch (err) {
          console.error("[ab-cadeau]", err);
          addData = null; // hieronder alsnog het normale pad
        }
      }

      if (!addData) {
        // Geen (geslaagde) parallelle add: het normale pad met dubbelcheck.
        // Snelpad zonder fetch: de base stuurt de vers gerenderde
        // cart-secties mee; staat de zojuist toegevoegde abonnementsregel
        // van dít product erin, dan is die HTML actueel en de cadeau-check
        // erop betrouwbaar. (Een abo-regel van een ánder product zegt niets:
        // die stond er misschien al.)
        let bestaat = null;
        if (data.sections) {
          const doc = sectionsDoc(data.sections);
          if (doc.querySelector(aboRowSelector(giftFor))) {
            bestaat = Boolean(doc.querySelector(cadeauRowSelector(giftFor)));
          }
        }
        if (bestaat === null) {
          const huidig = await fetch(routes.cart || "/cart.js").then((r) => (r.ok ? r.json() : null));
          bestaat = Boolean(
            huidig && huidig.items && huidig.items.some((item) => isCadeauVoor(item, giftFor))
          );
        }
        if (bestaat) return;

        const res = await fetch(routes.cart_add_url || "/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: cadeauFormData(variantId, giftFor),
        });
        if (!res.ok) throw new Error("cart/add failed: " + res.status);
        addData = await res.json();
      }

      // Quantity-vangnet: bestond er toch al een cadeauregel voor dit
      // product, dan heeft add.js de aantallen samengevoegd. Terug naar 1,
      // anders maakt de function de regel niet gratis.
      let sections = addData.sections;
      if (addData.quantity > 1 && addData.key) {
        const fd = new FormData();
        fd.append("id", addData.key);
        fd.append("quantity", "1");
        await fetch(withJs(routes.cart_change_url, "/cart/change"), {
          method: "POST",
          headers: { Accept: "application/json" },
          body: fd,
        });
        sections = undefined; // de meegerenderde HTML toont het oude aantal
      }

      // Secties alleen laten morphen als de abonnementsregel van dít product
      // er al in staat; werd onze add vóór die van de base verwerkt, dan zou
      // die HTML de abo-regel juist weer wegpoetsen (een abo-regel van een
      // ander product dat al in de cart zat maakt de HTML niet actueel). De
      // base-secties tonen in dat geval abonnement én cadeau al samen.
      if (sections && !sectionsDoc(sections).querySelector(aboRowSelector(giftFor))) {
        sections = data.sections || undefined;
      }

      // CartAddEvent is incrementeel (cart-count.js telt op): er komt precies
      // 1 cadeau bij. Met secties morpht cart-items-component synchroon.
      document.dispatchEvent(
        new CartAddEvent({}, this.id || "ab-cadeau", {
          itemCount: 1,
          source: "ab-cadeau",
          productId: variantId,
          variantId: variantId,
          sections: sections,
        })
      );
    } catch (err) {
      console.error("[ab-cadeau]", err);
    } finally {
      this._adding = false;
    }
  }

  /** Base-add mislukt (bv. voorraad): de parallel toegevoegde cadeauregel terugdraaien. */
  async onCartError() {
    const pending = this._pending;
    this._pending = null;
    if (!pending) return;
    try {
      const addData = await pending;
      if (addData && addData.key) {
        const routes = themeRoutes();
        const fd = new FormData();
        fd.append("id", addData.key);
        fd.append("quantity", "0");
        await fetch(withJs(routes.cart_change_url, "/cart/change"), {
          method: "POST",
          headers: { Accept: "application/json" },
          body: fd,
        });
      }
    } catch {
      // de parallelle add zelf faalde: niets terug te draaien
    }
  }
}

if (!customElements.get("ab-cadeau-component")) {
  customElements.define("ab-cadeau-component", AbCadeauComponent);
}

// Opruimen moet óók werken op pagina's zonder abonnementsblok (winkelwagenpagina,
// drawer op een collectiepagina). Daarom een losse listener, los van het element.
//
// Per product: verdwijnt de abonnementsregel van de capsules, dan gaat alleen
// de bewaarpot mee; het cadeau van een ander abonnement dat nog in de cart
// staat blijft gewoon staan.
//
// Instant: de base dispatcht zijn CartUpdateEvent (met de vers gerenderde
// cart-secties in data.sections) vóórdat hij zelf morpht. In die HTML is direct
// te zien welke cadeauregels hun abonnement kwijt zijn. Die regels gaan dan
// meteen synchroon uit beeld via een class op <html> plus een selector op hun
// key — die overleven de morph die ná ons draait — en de echte verwijdering
// volgt in één /cart/update.js.
let opruimBezig = false;

const OPRUIM_CLASS = "ab-cadeau-opruimen";

function opruimStijl() {
  let s = document.getElementById("ab-cadeau-stijl");
  if (!s) {
    s = document.createElement("style");
    s.id = "ab-cadeau-stijl";
    document.head.appendChild(s);
  }
  return s;
}

/** Alleen de weesregels verbergen; andere cadeaus blijven zichtbaar. */
function verbergRegels(keys) {
  const selectors = keys.map((key) => '[data-ab-cadeau-row][data-key="' + cssEscape(key) + '"]');
  opruimStijl().textContent =
    "html." + OPRUIM_CLASS + " :is(" + selectors.join(",") + ") { display: none !important; }";
  document.documentElement.classList.add(OPRUIM_CLASS);
}

async function verwijderCadeaus(keys, routes) {
  const fd = new FormData();
  for (const key of keys) {
    fd.append("updates[" + key + "]", "0");
  }
  const sectionIds = cartItemsSectionIds();
  if (sectionIds.length > 0) {
    fd.append("sections", sectionIds.join(","));
  }

  const res = await fetch(withJs(routes.cart_update_url, "/cart/update"), {
    method: "POST",
    headers: { Accept: "application/json" },
    body: fd,
  });
  if (!res.ok) throw new Error("cart/update failed: " + res.status);
  return res.json();
}

async function ruimCadeauOpZonderAbonnement(event) {
  if (opruimBezig) return;
  opruimBezig = true;
  let verborgen = false;
  try {
    const routes = themeRoutes();
    const detail = (event && event.detail) || {};
    const sections = (detail.data && detail.data.sections) || null;

    let keys = null;

    if (sections) {
      // Snelpad: beslissen op de meegestuurde sectie-HTML, zonder fetch. Dit
      // draait synchroon binnen de dispatch van de base, dus de class staat er
      // al vóór de base zijn eigen morph doet.
      const html = Object.values(sections).join("");
      if (!html.includes("data-ab-cadeau-row")) return;

      const doc = sectionsDoc(sections);
      const aboHandles = new Set(
        Array.from(doc.querySelectorAll("[data-ab-abo-row]")).map((el) => el.dataset.abAboFor || "")
      );
      keys = Array.from(doc.querySelectorAll("[data-ab-cadeau-row]"))
        .filter((el) => isWees(el.dataset.abCadeauFor || "", aboHandles))
        .map((el) => el.dataset.key)
        .filter(Boolean);
      keys = Array.from(new Set(keys));
      if (!keys.length) return;

      verbergRegels(keys);
      verborgen = true;
    } else {
      // Zonder secties (paginalaad, andere flows): cart bekijken — uit het
      // event als dat een echte cart meedraagt, anders via fetch.
      const cartUrl = routes.cart || "/cart.js";
      let cart = detail.resource && Array.isArray(detail.resource.items) ? detail.resource : null;
      if (!cart) cart = await fetch(cartUrl).then((r) => (r.ok ? r.json() : null));
      if (!cart || !cart.items) return;

      const aboHandles = new Set(
        cart.items.filter((i) => i.selling_plan_allocation).map((i) => i.handle)
      );
      const wezen = cart.items.filter(
        (i) => i.properties && i.properties._ab_cadeau && isWees(String(i.properties._ab_cadeau), aboHandles)
      );
      if (!wezen.length) return;

      keys = wezen.map((i) => i.key);
      verbergRegels(keys);
      verborgen = true;
    }

    const bijgewerkt = await verwijderCadeaus(keys, routes);
    document.dispatchEvent(
      new CartUpdateEvent(bijgewerkt, "ab-cadeau", {
        itemCount: bijgewerkt.item_count,
        source: "ab-cadeau",
        sections: bijgewerkt.sections || undefined,
      })
    );
  } catch (err) {
    console.error("[ab-cadeau] opruimen", err);
  } finally {
    // Het CartUpdateEvent hierboven laat cart-items-component synchroon
    // morphen (sections zitten erin), dus de regel bestaat hier al niet meer.
    // Bij een fout maakt dit de regel juist weer zichtbaar: nooit stil iets
    // verbergen dat nog in de cart zit.
    if (verborgen) document.documentElement.classList.remove(OPRUIM_CLASS);
    opruimBezig = false;
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const data = (event.detail && event.detail.data) || {};
  if (data.source === "ab-cadeau") return;
  ruimCadeauOpZonderAbonnement(event);
});

ruimCadeauOpZonderAbonnement();
